import { computed, ref } from 'vue';

/**
 * Generic composable for managing selection logic in the sharing hub.
 * Works for both partners and populations.
 *
 * @template T - The type of items being selected (e.g., PartnerWithSharing, PopulationWithSharing)
 */
export function useSharingHubSelection<T extends { id: number }>() {
  const selectedIds = ref<number[]>([]);

  const selectedIdsSet = computed(() => new Set(selectedIds.value));
  const hasSelection = computed(() => selectedIds.value.length > 0);

  function handleCheckboxChange(id: number, checked: boolean) {
    if (checked) {
      selectedIds.value.push(id);
    } else {
      selectedIds.value = selectedIds.value.filter((itemId) => itemId !== id);
    }
  }

  function handleSelectAll(
    checked: boolean,
    items: (T | { type: string; id: string })[],
  ) {
    if (checked) {
      const allIds = items
        .filter((item): item is T => !('type' in item))
        .map((item) => item.id);
      selectedIds.value = allIds;
    } else {
      selectedIds.value = [];
    }
  }

  function handleSubheaderSelectAll(
    sectionId: string,
    checked: boolean,
    items: (T | { type: string; id: string })[],
  ) {
    const sectionIndex = items.findIndex(
      (item) => 'type' in item && item.id === sectionId,
    );

    if (sectionIndex === -1) return;

    const idsInSection: number[] = [];
    for (let i = sectionIndex + 1; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      if ('type' in item) break;
      idsInSection.push(item.id);
    }

    if (checked) {
      const currentSet = new Set(selectedIds.value);
      const newSelections = idsInSection.filter((id) => !currentSet.has(id));
      selectedIds.value.push(...newSelections);
    } else {
      const removeSet = new Set(idsInSection);
      selectedIds.value = selectedIds.value.filter((id) => !removeSet.has(id));
    }
  }

  function clearSelection() {
    selectedIds.value = [];
  }

  return {
    selectedIds,
    selectedIdsSet,
    hasSelection,
    handleCheckboxChange,
    handleSelectAll,
    handleSubheaderSelectAll,
    clearSelection,
  };
}
